import React, { FunctionComponent } from 'react';
import { startCase } from 'lodash';
import { FNIType } from '@upstart/harmony/lib/constants';
import { DealerLaunchStepProps } from '../types';
import { StepInfoGateProps } from './DealerLaunchInfoGate';
import { Dealer } from '../../../types/dealerTypes';
import { Select } from '../../Input/Select/Select';
import { TextField } from '../../Input/TextField/TextField';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import { FNI_TYPE_NAME } from '../../../utils/constants';
import { DealerLaunchFormCardLayout } from '../DealerLaunchFormCardLayout';
import styles from '../styles.module.scss';

export const DealerLaunchIntegrationFnIInfoGate: StepInfoGateProps = {
  title: "Let's Integrate with your F&I System",
  cardTitle: 'Before we connect with your F&I System',
  cardDescription:
    'Upstart Auto Retail pushes credit applications to your F&I provider to seamlessly integrate with your business. To do this, Upstart Auto Retail will ask for your F&I provider dealer id. Once this has been completed, Upstart Auto Retail will integrate with your F&I provider.',
  continueText: 'I Understand',
};

export const DealerLaunchIntegrationFnI: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  prevStep,
  nextStep,
}) => {
  const form = useLaunchpadForm<Dealer>({ defaultValues: dealer });
  const fniType = form.watch('fniType') as FNIType;

  const generateNotificationText = (type: FNIType) => {
    let fniTypeText;
    switch (type) {
      case FNIType.ROUTEONE:
        fniTypeText = 'RouteOne';
        break;
      case FNIType.DEALERTRACK:
        fniTypeText = 'Dealertrack';
        break;
      default:
        return null;
    }

    // show the notification text section when the user chooses RouteOne or Dealertrack
    return (
      <div>
        <p className={styles.colorFniNotification}>
          Great news! Because {fniTypeText} integrates with Upstart Auto Retail,
          your dealership is eligible for Real-Time Approvals. We&apos;ll help
          you set up Real-Time Approvals in {fniTypeText} later.
        </p>
      </div>
    );
  };

  return (
    <DealerLaunchFormCardLayout
      form={form}
      title={DealerLaunchIntegrationFnIInfoGate.title}
      subTitle="To seamlessly integrate with your business, Upstart Auto Retail pushes credit applications to your F&I system."
      cardTitle="Which F&I System do you use?"
      prevStep={prevStep}
      onFormSubmit={nextStep}
    >
      <div className={styles.stepRow}>
        <Select
          label="F&I Provider"
          tooltip="Where you submit credit applications to run credit."
          name="fniType"
          width="md"
          form={form}
          options={Object.values(FNIType).map((type) => ({
            label: FNI_TYPE_NAME[type] || startCase(type),
            value: type,
          }))}
          addEmptyOption
          emptyOptionText="Select"
          validation={{ required: true }}
        />
        {fniType === FNIType.CUDL && (
          <TextField
            label="CUDL ID"
            tooltip="If your ID is unknown to you, contact your F&I provider."
            name="cudl.cudlId"
            width="md"
            form={form}
            validation={{ required: true }}
          />
        )}
        {fniType === FNIType.DEALERTRACK && (
          <TextField
            label="Dealertrack ID"
            tooltip="If your ID is unknown to you, contact your F&I provider."
            name="dealertrack.dealertrackId"
            width="md"
            form={form}
            validation={{ required: true }}
          />
        )}
        {fniType === FNIType.ROUTEONE && (
          <TextField
            label="RouteOne ID"
            tooltip="If your ID is unknown to you, contact your F&I provider."
            name="routeone.routeOneId"
            width="md"
            form={form}
            validation={{ required: true }}
          />
        )}
      </div>
      {generateNotificationText(fniType)}
    </DealerLaunchFormCardLayout>
  );
};
